<script>
   /**********************************************************************/
   /* PlotSeries                                                         */
   /* --------------------                                               */
   /* shows line-scatter-bar series with selection and color grouping    */
   /* possibility                                                        */
   /**********************************************************************/

   import { getContext } from 'svelte';
   import { range } from 'mdatools/stat';
   import { val2p, checkCoords, transformCoords, transformObjects, handleClick } from 'svelte-plots-basic/utils';
   import { LABELS_MARGIN } from 'svelte-plots-basic/constants';

   import { LINE_COLORS, LABELS_COLOR, LABELS_COLOR_SELECTED, MAX_LABEL_SYMBOLS } from './Defaults.js';

   let {
      xValues,                            // vector or array with x-values
      yValues,                            // vector or array with y-values
      plotType,                           // type of plot ('p', 'l', 'h', 'b')
      labels,                             // array with labels for plot elements
      showLabels,                         // show or not labels for plot elements
      shortenLabels = true,               // do we need to shorten labels
      lineWidth = 1.0,                    // line height
      markerSize = null,                  // marker size (if null will be set automatically)
      markerWidth = 2.0,                  // thickness of marker
      pch = '●',                          // point character (marker)
      colors = undefined,                 // color for every point or bar
      mainColor = LINE_COLORS['cal'],     // main color if color grouping is not used
      faceColor = null,                   // face color for the points
      selected = -1,                      // selected data point
      xRange = $bindable(),               // smallest y-value
      yRange = $bindable(),               // largest x-value
      callbacks,                          // callback for onclick events
   } = $props();


   // handler for select event
   function select(e) {
      if (!callbacks || !callbacks.select) return;
      handleClick(e, e.target.tagName, callbacks.select);
   }

   // handler for unselect event
   function unselect(e) {
      if (!callbacks || !callbacks.unselect) return;
      callbacks.unselect();
   }

   // get axes context
   const axes = getContext('axes');

   // check coordinates
   const xv = $derived(checkCoords(xValues, 'PlotSeries'));
   const yv = $derived(xv ? checkCoords(yValues, 'PlotSeries', xv.length) : null);

   // compute x-limits
   $effect(() => {
      xRange = xv ? range(xv) :  null;
      yRange = yv ? range(yv) :  null;
   })

   // face color and size of markers
   const fcol = $derived(faceColor === null ? (plotType === 'p' ? 'transparent' : 'white') : faceColor);
   const msize = $derived(!markerSize ? (plotType==='p' ? 1 : 0.85) : markerSize);

   // reactive calculations for bar plot parameters
   const m = $derived(LABELS_MARGIN[axes.scales().plot]);
   const p0 = $derived(transformCoords([0.0], axes.tY())[0]);
   const bw = $derived(transformObjects([0.8], axes.tX())[0]);
   const bhw = $derived(bw / 2.0);
   const dy = $derived(m);

   // reactive calculations for coordinates
   let lp = $derived( plotType === 'l' || plotType === 'b' ? val2p(xv, yv, axes.tX(), axes.tY()) : null);
   let px = $derived(xv ? transformCoords(xv, axes.tX()) : null);
   let py = $derived(yv ? transformCoords(yv, axes.tY()) : null);

   // truncate the labels
   let labelsShort = $derived(labels && Array.isArray(labels) ?
      (shortenLabels ? labels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : labels) :
      null
   );

   // reactive expression for colors
   let pc = $derived(colors && colors.length === xv.length ? colors : Array(xv.length).fill(mainColor));

   // styles for points, bars, labels, etc.
   const ptStyle = $derived(`font-size:${msize}em;fill:${fcol};cursor:default;text-anchor:middle;stroke-width:${markerWidth}px;user-select:none;`);
   const lbStyle = `font-size:0.85em;fill:${LABELS_COLOR};cursor:default;text-anchor:middle;stroke:${LABELS_COLOR};stroke-width:0.1px;user-select:none;`;
   const slStyle = `font-size:1.2em;fill:none;text-anchor:middle;text-anchor:middle;cursor:default;stroke-width:3px;user-select:none;`;
   const lineStyleStr = `fill:transparent;stroke-width: ${lineWidth}px;`;
   const barsStyleStr = `stroke-width:${lineWidth}px;`;
</script>

{#if px && py}

   {#if showLabels && labelsShort}
      <!-- labels -->
      <g class="series series-textlabels" style={lbStyle}>
      {#each px as v, i}
      <text x={px[i]} y={py[i]} dy={yv.v[i] < 0 ? dy : -dy}>{labelsShort[i]}</text>
      {/each}
      </g>
   {/if}


   {#if plotType === 'l' || plotType === 'b'}
      <!-- line plot -->
      <g class="series series-line" style={lineStyleStr}>
         <polyline class="line" points={lp} stroke={mainColor}/>
      </g>
   {/if}

   {#if plotType === 'h'}
      <!-- bar plot -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <g class="series series-bar" style={barsStyleStr} onclick={select}>
      {#each px as v, i}
      <rect x={px[i] - bhw} y={(yv.v[i] < 0) ? p0 : py[i]} fill={pc[i]} width={bw} height={yv.v[i] < 0 ? py[i] - p0 : p0 - py[i]} />
      {/each}
      </g>
   {/if}

   {#if plotType === 'p' || plotType === 'b'}
      <!-- scatter/points plot -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <g class="series series-points series-points1" style={ptStyle} onclick={select}>
      {#each px as v, i}
      <text x={px[i]} y={py[i]} fill={faceColor} stroke={pc[i]}>{pch}</text>
      {/each}
      </g>
   {/if}


   {#if selected >= 0}
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <rect x={0} y={0} width='100%' height='100%' fill="#ffffffa0" onclick={unselect}/>
      {#if plotType !== 'h'}
      <g class="series series-points series-points1" style={slStyle}>
      <text x={px[selected]} y={py[selected]} stroke={pc[selected]} >{pch}</text>
      </g>
      {:else}
      <g class="series series-bar" style={barsStyleStr}>
      <rect stroke={pc[selected]} fill={pc[selected]} x={px[selected] - bhw} y={(yv.v[selected] < 0) ? p0 : py[selected]} width={bw} height={yv.v[selected] < 0 ? py[selected] - p0 : p0 - py[selected]} />
      </g>
      {/if}

      {#if showLabels && labelsShort && labelsShort.length > 0}
      <g class="series series-text" style={lbStyle}>
      <text stroke={LABELS_COLOR_SELECTED} fill={LABELS_COLOR_SELECTED} x={px[selected]} y={py[selected]} dy={yv.v[selected] < 0 ? dy : -dy}>{labelsShort[selected]}</text>
      </g>
      {/if}
   {/if}
{/if}